import React from 'react'
import { useForm } from '@inertiajs/react'
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Stack,
} from '@mui/material'
import AuthLayout from '@/components/layouts/AuthLayout'
import { SPACING } from '@/theme'

interface Props {
  user: {
    email: string
    password: string
    password_confirmation: string
  }
  account: {
    name: string
  }
  errors?: {
    user?: Record<string, string[]>
    account?: Record<string, string[]>
  }
}

export default function Setup({ user, account, errors }: Props) {
  const { data, setData, post, processing } = useForm({
    user: {
      email: user.email,
      password: '',
      password_confirmation: '',
    },
    account: {
      name: account.name,
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/setup')
  }

  return (
    <AuthLayout>
      <Card sx={{ minWidth: 400, maxWidth: 500 }}>
        <CardContent sx={{ p: SPACING.form }}>
          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            Welcome to S3 Proxy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: SPACING.section }}>
            Let's set up your first account and administrator user.
          </Typography>

          <Stack component="form" onSubmit={handleSubmit} spacing={SPACING.gap.md}>
            <Typography variant="subtitle2" fontWeight="medium">
              Account Details
            </Typography>

            <TextField
              fullWidth
              label="Account Name"
              value={data.account.name}
              onChange={(e) => setData('account', { ...data.account, name: e.target.value })}
              error={!!errors?.account?.name}
              helperText={errors?.account?.name?.[0]}
              placeholder="My Company"
            />

            <Divider />

            <Typography variant="subtitle2" fontWeight="medium">
              Administrator Account
            </Typography>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={data.user.email}
              onChange={(e) => setData('user', { ...data.user, email: e.target.value })}
              error={!!errors?.user?.email}
              helperText={errors?.user?.email?.[0]}
              autoComplete="email"
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={data.user.password}
              onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
              error={!!errors?.user?.password}
              helperText={errors?.user?.password?.[0]}
              autoComplete="new-password"
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={data.user.password_confirmation}
              onChange={(e) => setData('user', { ...data.user, password_confirmation: e.target.value })}
              error={!!errors?.user?.password_confirmation}
              helperText={errors?.user?.password_confirmation?.[0]}
              autoComplete="new-password"
            />

            {(errors?.user || errors?.account) && !errors?.user?.email && !errors?.user?.password && !errors?.account?.name && (
              <Alert severity="error">
                There was an error with your submission. Please check your inputs.
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={processing}
            >
              {processing ? 'Setting up...' : 'Complete Setup'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
